import { farmService, Farm } from '../../../services/farm/farmService';
import { FarmOrganizationData } from '../../farm/types/farmTypes';

// Strategy interface for organization operations
interface OrganizationStrategy {
  create(data: any): Promise<Farm>;
  search(filters: any): Promise<Farm[]>;
  getById(id: string): Promise<Farm | null>;
  update(id: string, data: any): Promise<Farm | null>;
  toggleFavorite(id: string): Promise<Farm | null>;
  searchByName(name: string, userId?: string): Promise<Farm[]>;
  searchByOwner(ownerId: string): Promise<Farm[]>;
  searchByCollaborator(userId: string): Promise<Farm[]>;
}

// Farm strategy implementation
class FarmStrategy implements OrganizationStrategy {
  async create(data: FarmOrganizationData): Promise<Farm> {
    if (!data.owner_id) {
      throw new Error('Owner ID é obrigatório para criar uma fazenda');
    }
    
    const farmData = {
      owner_id: data.owner_id,
      name: data.name,
      registration_number: data.registration_number || '',
      farm_type: data.farm_type || 'mixed',
      production_focus: data.production_focus || 'mixed_production',
      country_code: data.country_code || 'BR',
      location: {
        address: data.location?.address || '',
        region: data.location?.region || '',
        postal_code: data.location?.postal_code || '',
        coordinates: {
          latitude: data.location?.coordinates?.latitude ?? 0,
          longitude: data.location?.coordinates?.longitude ?? 0
        }
      },
      area_hectares: data.area_hectares || 0,
      capacity_head: data.capacity_head || 0,
      facilities: data.facilities || ['feed_storage', 'veterinary_facilities'],
      certifications: data.certifications || [],
      contact_info: {
        phone: data.contact_info?.phone || '',
        email: data.contact_info?.email || '',
        emergency_contact: data.contact_info?.emergency_contact || ''
      },
      is_active: true
    };

    const response = await farmService.createFarm(farmData);
    
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to create farm');
    }
    
    return response.data;
  }

  async search(filters: any = {}): Promise<Farm[]> {
    const response = await farmService.search({
      bind_vars: {
        '@collection': 'Farms',
        ...filters
      },
      query: 'FOR doc IN @@collection RETURN doc'
    });
    return response.success ? response.data || [] : [];
  }

  async searchByOwner(ownerId: string): Promise<Farm[]> {

    
    const response = await farmService.searchByOwner(ownerId);
    
    

    
    const result = response.success ? response.data || [] : [];

    
    return result;
  }

  async getById(id: string): Promise<Farm | null> {
    const response = await farmService.search({
      bind_vars: {
        '@collection': 'Farms',
        key: id
      },
      query: 'FOR doc IN @@collection FILTER doc._key == @key RETURN doc'
    });
    return response.success && response.data?.length > 0 ? response.data[0] : null;
  }

  async update(id: string, data: any): Promise<Farm | null> {
    const response = await farmService.updateByKey(id, data);
    return response.success ? response.data : null;
  }

  async toggleFavorite(id: string): Promise<Farm | null> {
    const current = await this.getById(id);
    if (!current) return null;
    
    const response = await farmService.updateByKey(id, {
      is_favorite: !current.is_favorite
    });
    return response.success ? response.data : null;
  }

  async searchByName(name: string, userId?: string): Promise<Farm[]> {
    const response = await farmService.searchByName(name, userId);
    return response.success ? response.data || [] : [];
  }

  async searchByCollaborator(userId: string): Promise<Farm[]> {
    const response = await farmService.aggregateByUser(userId);
    if (!response.success) {
      throw new Error(response.error || 'Failed to aggregate farms');
    }

    const rows = Array.isArray(response.data) ? response.data : [];
    const aggregate = rows[0] || {};
    const assignedEntries = Array.isArray((aggregate as any).assigned_farms) ? (aggregate as any).assigned_farms : [];

    const assignedFarms = assignedEntries
      .map((entry: any) => {
        const farm = entry?.farm;
        if (!farm) return null;
        const roles = Array.isArray(entry?.roles) ? entry.roles : [];
        return { ...farm, assigned_roles: roles };
      })
      .filter(Boolean) as Farm[];

    return assignedFarms.filter((farm) => farm.owner_id !== userId);
  }
}

// Organization router using Strategy Pattern
export class OrganizationRouter {
  private strategies: Record<string, OrganizationStrategy> = {
    farm: new FarmStrategy(),
    dairy: new FarmStrategy(), // Use farm strategy until dairy service exists
    slaughterhouse: new FarmStrategy(), // Use farm strategy until slaughterhouse service exists
    cooperative: new FarmStrategy() // Use farm strategy until cooperative service exists
  };

  private getStrategy(type: string): OrganizationStrategy {
    return this.strategies[type] || this.strategies.farm;
  }

  async create(type: string, data: any): Promise<Farm> {
    // Iniciando criação de organização
    
    const strategy = this.getStrategy(type);
    // Strategy selecionada para o tipo
    
    const result = await strategy.create(data);
    
    // Organização criada com sucesso
    
    return result;
  }

  async search(type: string = 'farm', filters: any = {}): Promise<Farm[]> {
    return await this.getStrategy(type).search(filters);
  }

  async getById(id: string, type: string = 'farm'): Promise<Farm | null> {
    return await this.getStrategy(type).getById(id);
  }

  async update(id: string, data: any, type: string = 'farm'): Promise<Farm | null> {
    return await this.getStrategy(type).update(id, data);
  }

  async toggleFavorite(id: string, type: string = 'farm'): Promise<Farm | null> {
    return await this.getStrategy(type).toggleFavorite(id);
  }

  async searchByName(name: string, type: string = 'farm', userId?: string): Promise<Farm[]> {
    return await this.getStrategy(type).searchByName(name, userId);
  }

  async searchByCollaborator(userId: string, type: string = 'farm'): Promise<Farm[]> {
    return await this.getStrategy(type).searchByCollaborator(userId);
  }

  async getAllOrganizations(ownerId?: string): Promise<Farm[]> {

    
    let organizations: Farm[];
    if (ownerId) {
      const response = await farmService.aggregateByUser(ownerId);
      if (!response.success) {
        throw new Error(response.error || 'Failed to aggregate farms');
      }

      const rows = Array.isArray(response.data) ? response.data : [];
      const aggregate = rows[0] || {};
      const ownedOrganizations = Array.isArray((aggregate as any).owner_farms) ? (aggregate as any).owner_farms : [];
      const assignedEntries = Array.isArray((aggregate as any).assigned_farms) ? (aggregate as any).assigned_farms : [];

      const assignedOrganizations = assignedEntries
        .map((entry: any) => {
          const farm = entry?.farm;
          if (!farm) return null;
          const roles = Array.isArray(entry?.roles) ? entry.roles : [];
          return { ...farm, assigned_roles: roles };
        })
        .filter(Boolean) as Farm[];

      const allOrganizations = [...ownedOrganizations, ...assignedOrganizations];
      const uniqueOrganizations = allOrganizations.filter((org, index, self) => 
        index === self.findIndex(o => (o as any)._key === (org as any)._key)
      );

      organizations = uniqueOrganizations;
    } else {

      organizations = await this.search('farm');
    }
    

    
    return organizations;
  }

  async getRecentOrganizations(limit: number = 5, ownerId?: string): Promise<Farm[]> {

    
    const organizations = await this.getAllOrganizations(ownerId);
    const recentOrganizations = organizations
      .sort((a, b) => new Date(b.updated_at || '').getTime() - new Date(a.updated_at || '').getTime())
      .slice(0, limit);
    

    
    return recentOrganizations;
  }

  async getFavoriteOrganizations(ownerId?: string): Promise<Farm[]> {

    
    const organizations = await this.getAllOrganizations(ownerId);
    const favoriteOrganizations = organizations.filter(org => org.is_favorite);
    

    
    return favoriteOrganizations;
  }
}

export const organizationRouter = new OrganizationRouter();
